import { useState, useEffect, useCallback } from 'react';
import { syncTournament } from '../utils/syncManager';
import { saveScore, getPlayerScores } from '../utils/scoreManager';

export function useOfflineScoring(tournamentId: string) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  
  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && tournamentId) {
      handleSync();
    }
  }, [isOnline, tournamentId]);
  
  // Save a score (works online or offline)
  const handleSaveScore = useCallback((playerId: string, hole: number, strokes: number) => {
    const success = saveScore(tournamentId, playerId, hole, strokes);
    
    // Try to sync immediately if online
    if (success && isOnline) {
      handleSync();
    }
    
    return success;
  }, [tournamentId, isOnline]);
  
  // Get scores for a player
  const getScores = useCallback((playerId: string) => {
    return getPlayerScores(tournamentId, playerId);
  }, [tournamentId]);
  
  // Manually trigger sync
  const handleSync = useCallback(async () => {
    if (isSyncing || !isOnline) return;
    
    setIsSyncing(true);
    setSyncError(null);
    
    try {
      const result = await syncTournament(tournamentId);
      
      if (result.success) {
        setLastSyncTime(Date.now());
      } else {
        setSyncError(result.errors.join(', '));
      }
      
      return result;
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : String(error));
      return { success: false, syncedCount: 0, errors: [String(error)] };
    } finally {
      setIsSyncing(false);
    }
  }, [tournamentId, isOnline, isSyncing]);
  
  return {
    isOnline,
    isSyncing,
    syncError,
    lastSyncTime,
    saveScore: handleSaveScore,
    getScores,
    sync: handleSync
  };
} 